import React, { useEffect, useRef, useState, useCallback } from 'react'
import { Box, IconButton, Typography, Tooltip } from '@mui/material'
import PlayArrowIcon from '@mui/icons-material/PlayArrow'
import PauseIcon from '@mui/icons-material/Pause'
import DownloadIcon from '@mui/icons-material/Download'
import ReplayIcon from '@mui/icons-material/Replay'
import { useTranslation } from 'react-i18next'
import { tokens } from '../../theme/tokens'

interface TtsAudioPlayerProps {
  audioBlob: Blob | null
  fileName?: string
}

const BAR_WIDTH = 3
const BAR_GAP = 1
const CANVAS_HEIGHT = 80

const TtsAudioPlayer: React.FC<TtsAudioPlayerProps> = ({
  audioBlob,
  fileName = 'tts-audio.wav'
}) => {
  const audioRef = useRef<HTMLAudioElement | null>(null)
  const canvasRef = useRef<HTMLCanvasElement | null>(null)
  const waveformRef = useRef<number[]>([])
  const blobUrlRef = useRef<string | null>(null)
  const rafRef = useRef<number>(0)

  const { t } = useTranslation()
  const [playing, setPlaying] = useState(false)
  const [progress, setProgress] = useState(0)
  const [duration, setDuration] = useState(0)
  const [hoverX, setHoverX] = useState<number | null>(null)

  // Scale canvas for HiDPI displays
  const scaleCanvas = useCallback(() => {
    const canvas = canvasRef.current
    if (!canvas) return
    const dpr = window.devicePixelRatio || 1
    const rect = canvas.getBoundingClientRect()
    canvas.width = Math.round(rect.width * dpr)
    canvas.height = Math.round(CANVAS_HEIGHT * dpr)
    const ctx = canvas.getContext('2d')
    if (ctx) ctx.scale(dpr, dpr)
  }, [])

  // Compute normalized bar amplitudes from decoded audio
  const computeWaveform = useCallback((decoded: AudioBuffer): number[] => {
    const canvas = canvasRef.current
    if (!canvas) return []

    const rect = canvas.getBoundingClientRect()
    const logicalWidth = rect.width

    const data = decoded.getChannelData(0)
    const barCount = Math.floor(logicalWidth / (BAR_WIDTH + BAR_GAP))
    const samplesPerBar = Math.floor(data.length / barCount)
    const bars: number[] = []

    for (let i = 0; i < barCount; i++) {
      let sum = 0
      const offset = i * samplesPerBar
      for (let j = 0; j < samplesPerBar; j++) {
        sum += Math.abs(data[offset + j] ?? 0)
      }
      bars.push(sum / samplesPerBar)
    }

    // Normalize to 0–1 range
    const peak = Math.max(...bars, 0.001)
    return bars.map(v => v / peak)
  }, [])

  // Draw waveform bars with progress overlay (uses logical coordinates, ctx is pre-scaled for dpr)
  const drawWaveform = useCallback((progressPct: number, hover: number | null) => {
    const canvas = canvasRef.current
    if (!canvas) return

    const ctx = canvas.getContext('2d')
    if (!ctx) return

    const bars = waveformRef.current
    if (bars.length === 0) return

    const dpr = window.devicePixelRatio || 1
    const w = canvas.width / dpr   // logical width
    const h = canvas.height / dpr  // logical height
    const midY = h / 2
    const barStep = BAR_WIDTH + BAR_GAP
    const progressX = (progressPct / 100) * w

    ctx.clearRect(0, 0, w, h)

    // Draw each bar
    for (let i = 0; i < bars.length; i++) {
      const x = i * barStep
      const amplitude = bars[i]
      // Min bar height of 2px, max fills 90% of half-height (symmetrical)
      const barH = Math.max(2, amplitude * midY * 0.9)

      const isPast = x + BAR_WIDTH <= progressX
      const isHovered = hover !== null && Math.abs(x + BAR_WIDTH / 2 - hover) < barStep

      if (isPast) {
        ctx.fillStyle = tokens.colors.tts.main        // #004a57 — solid played
      } else if (isHovered) {
        ctx.fillStyle = tokens.colors.tts.light + 'b0' // #00758a hover highlight
      } else {
        ctx.fillStyle = tokens.colors.tts.main + '55'  // #004a57 — visible idle (~33%)
      }

      // Symmetrical bars around center line
      const radius = Math.min(1.5, BAR_WIDTH / 2)
      roundRect(ctx, x, midY - barH, BAR_WIDTH, barH * 2, radius)
    }

    // Playhead line
    if (progressPct > 0 && progressPct < 100) {
      ctx.fillStyle = tokens.colors.tts.light
      ctx.fillRect(Math.round(progressX) - 1, 0, 2, h)

      // Playhead dot
      ctx.beginPath()
      ctx.arc(Math.round(progressX), midY, 5, 0, Math.PI * 2)
      ctx.fillStyle = tokens.colors.tts.light
      ctx.fill()
      ctx.strokeStyle = '#fff'
      ctx.lineWidth = 2
      ctx.stroke()
    }

    // Hover cursor line (only when not playing — check audio element directly)
    const audio = audioRef.current
    if (hover !== null && (!audio || audio.paused)) {
      ctx.fillStyle = tokens.colors.tts.main + '50'
      ctx.fillRect(Math.round(hover) - 0.5, 0, 1, h)
    }
  }, [])  // No deps — reads playing state from audioRef to avoid re-renders

  // Animation loop for smooth progress during playback
  const animate = useCallback(() => {
    const audio = audioRef.current
    if (!audio || audio.paused) return

    const pct = audio.duration > 0 ? (audio.currentTime / audio.duration) * 100 : 0
    setProgress(pct)
    drawWaveform(pct, null)
    rafRef.current = requestAnimationFrame(animate)
  }, [drawWaveform])

  // Build blob URL and decode waveform whenever audioBlob changes
  useEffect(() => {
    if (!audioBlob) return

    // Revoke previous URL
    if (blobUrlRef.current) {
      URL.revokeObjectURL(blobUrlRef.current)
    }

    const url = URL.createObjectURL(audioBlob)
    blobUrlRef.current = url

    if (audioRef.current) {
      audioRef.current.src = url
      audioRef.current.load()
    }

    // Scale canvas for HiDPI, then decode and compute waveform bars
    scaleCanvas()
    audioBlob.arrayBuffer().then((buffer) => {
      const audioCtx = new AudioContext()
      audioCtx.decodeAudioData(buffer, (decoded) => {
        waveformRef.current = computeWaveform(decoded)
        drawWaveform(0, null)
        audioCtx.close()
      })
    })

    setPlaying(false)
    setProgress(0)
    setDuration(0)

    return () => {
      cancelAnimationFrame(rafRef.current)
    }
  }, [audioBlob, scaleCanvas, computeWaveform, drawWaveform])

  // Revoke blob URL on unmount
  useEffect(() => {
    return () => {
      if (blobUrlRef.current) {
        URL.revokeObjectURL(blobUrlRef.current)
      }
      cancelAnimationFrame(rafRef.current)
    }
  }, [])

  // Redraw on progress or hover change when NOT playing (playing uses rAF)
  useEffect(() => {
    if (!playing) {
      drawWaveform(progress, hoverX)
    }
  }, [progress, hoverX, playing, drawWaveform])

  const handlePlayPause = useCallback(() => {
    const audio = audioRef.current
    if (!audio) return
    if (audio.paused) {
      if (audio.ended) audio.currentTime = 0
      audio.play().catch(() => {
        setPlaying(false)
      })
    } else {
      audio.pause()
    }
  }, [])

  const handlePlay = useCallback(() => {
    setPlaying(true)
    rafRef.current = requestAnimationFrame(animate)
  }, [animate])

  const handlePause = useCallback(() => {
    setPlaying(false)
    cancelAnimationFrame(rafRef.current)
    // Draw final frame
    const audio = audioRef.current
    if (audio && audio.duration > 0) {
      const pct = (audio.currentTime / audio.duration) * 100
      setProgress(pct)
      drawWaveform(pct, null)
    }
  }, [drawWaveform])

  const handleEnded = useCallback(() => {
    setPlaying(false)
    cancelAnimationFrame(rafRef.current)
    setProgress(0)
    drawWaveform(0, null)
  }, [drawWaveform])

  const handleLoadedMetadata = useCallback(() => {
    const audio = audioRef.current
    if (audio) setDuration(audio.duration)
  }, [])

  // Click on waveform to seek
  const handleCanvasClick = useCallback((e: React.MouseEvent<HTMLCanvasElement>) => {
    const audio = audioRef.current
    const canvas = canvasRef.current
    if (!audio || !canvas || !audio.duration) return

    const rect = canvas.getBoundingClientRect()
    const x = e.clientX - rect.left
    const ratio = x / rect.width
    const newTime = ratio * audio.duration
    audio.currentTime = newTime
    const pct = ratio * 100
    setProgress(pct)
    drawWaveform(pct, null)
  }, [drawWaveform])

  const handleCanvasHover = useCallback((e: React.MouseEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current
    if (!canvas) return
    const rect = canvas.getBoundingClientRect()
    // Map to logical width (rect.width matches logical space after dpr scaling)
    const dpr = window.devicePixelRatio || 1
    const logicalWidth = canvas.width / dpr
    const x = (e.clientX - rect.left) / rect.width * logicalWidth
    setHoverX(x)
  }, [])

  const handleCanvasLeave = useCallback(() => {
    setHoverX(null)
  }, [])

  const formatTime = (seconds: number): string => {
    if (!isFinite(seconds)) return '0:00'
    const m = Math.floor(seconds / 60)
    const s = Math.floor(seconds % 60)
    return `${m}:${s.toString().padStart(2, '0')}`
  }

  if (!audioBlob) return null

  return (
    <Box
      sx={{
        borderRadius: 3,
        backgroundColor: 'rgba(0, 74, 87, 0.04)',
        border: '1px solid rgba(0, 74, 87, 0.15)',
        overflow: 'hidden',
      }}
    >
      {/* Hidden audio element */}
      <audio
        ref={audioRef}
        onPlay={handlePlay}
        onPause={handlePause}
        onEnded={handleEnded}
        onLoadedMetadata={handleLoadedMetadata}
        style={{ display: 'none' }}
      />

      {/* Interactive waveform */}
      <Box
        sx={{
          px: 2,
          pt: 2,
          pb: 0.5,
          cursor: 'pointer',
          '&:hover': { backgroundColor: 'rgba(0, 74, 87, 0.02)' },
          transition: 'background-color 0.15s',
        }}
      >
        <canvas
          ref={canvasRef}
          onClick={handleCanvasClick}
          onMouseMove={handleCanvasHover}
          onMouseLeave={handleCanvasLeave}
          style={{
            width: '100%',
            height: CANVAS_HEIGHT,
            borderRadius: 6,
            display: 'block',
            cursor: 'pointer',
          }}
        />
      </Box>

      {/* Controls row */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 2, pb: 1.5, pt: 0.5 }}>
        <IconButton
          onClick={handlePlayPause}
          size="small"
          aria-label={playing ? t('tts.pause', 'Pause') : t('tts.play', 'Play')}
          sx={{
            color: '#fff',
            background: tokens.gradients.tts,
            width: 36,
            height: 36,
            '&:hover': {
              background: `linear-gradient(135deg, ${tokens.colors.tts.light} 0%, ${tokens.colors.tts.main} 100%)`,
            },
            flexShrink: 0,
          }}
        >
          {playing ? <PauseIcon fontSize="small" /> : <PlayArrowIcon fontSize="small" />}
        </IconButton>

        {/* Replay from start */}
        {!playing && progress === 0 && duration > 0 && (
          <Tooltip title={t('tts.replay', 'Replay')}>
            <IconButton
              onClick={() => {
                const audio = audioRef.current
                if (!audio) return
                audio.currentTime = 0
                audio.play().catch(() => setPlaying(false))
              }}
              size="small"
              sx={{ color: tokens.colors.tts.main, flexShrink: 0 }}
            >
              <ReplayIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}

        <Typography
          variant="caption"
          sx={{
            color: 'text.secondary',
            fontFamily: tokens.typography.fontFamilyMono,
            fontSize: '0.75rem',
            flexShrink: 0,
            ml: 0.5,
          }}
        >
          {formatTime(duration > 0 ? (progress / 100) * duration : 0)}
          <Box component="span" sx={{ mx: 0.5, opacity: 0.4 }}>/</Box>
          {formatTime(duration)}
        </Typography>

        <Box sx={{ flex: 1 }} />

        {blobUrlRef.current && (
          <Tooltip title={t('tts.downloadAudio', 'Download audio')}>
            <IconButton
              component="a"
              href={blobUrlRef.current}
              download={fileName}
              size="small"
              sx={{
                color: 'text.secondary',
                flexShrink: 0,
                '&:hover': { color: tokens.colors.tts.main }
              }}
            >
              <DownloadIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Box>
    </Box>
  )
}

// Draw a rounded rectangle (used for waveform bars)
function roundRect(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number,
) {
  if (h <= 0) return
  r = Math.min(r, w / 2, h / 2)
  ctx.beginPath()
  ctx.moveTo(x + r, y)
  ctx.lineTo(x + w - r, y)
  ctx.quadraticCurveTo(x + w, y, x + w, y + r)
  ctx.lineTo(x + w, y + h - r)
  ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h)
  ctx.lineTo(x + r, y + h)
  ctx.quadraticCurveTo(x, y + h, x, y + h - r)
  ctx.lineTo(x, y + r)
  ctx.quadraticCurveTo(x, y, x + r, y)
  ctx.closePath()
  ctx.fill()
}

export default TtsAudioPlayer
